import { marked } from 'marked'

import { logService, LOG_CATEGORIES } from '@/services/logService'

export interface StaticPage {
  id: string
  title: string
  htmlContent?: string
}

class StaticPageService {
  private pages: Map<string, StaticPage> = new Map()
  private manifest: Record<string, { file: string; hash: string }> | null = null

  constructor() {
    // Configure marked for better HTML output
    marked.setOptions({
      breaks: true,
      gfm: true,
      async: false, // Ensure synchronous parsing
    })
  }

  private async getCacheBuster(filePath: string): Promise<string> {
    if (import.meta.env.DEV) {
      return String(Date.now())
    }

    // Generate hash of the file content
    const response = await fetch(filePath)

    const content = await response.text()
    const encoder = new TextEncoder()
    const data = encoder.encode(content)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')

    // Return first 8 characters of hash for brevity
    return hashHex.substring(0, 8)
  }

  /**
   * Load the static pages manifest which contains hashes for cache busting
   */
  private async loadManifest(): Promise<Record<string, { file: string; hash: string }> | null> {
    if (this.manifest) return this.manifest

    try {
      const manifestPath = '/_cache/static-pages/manifest.json'
      // In dev, avoid caching; in prod, allow default caching as content is hashed
      const url = import.meta.env.DEV ? `${manifestPath}?t=${Date.now()}` : manifestPath
      const res = await fetch(url)
      if (!res.ok) return null
      const data = (await res.json()) as Record<string, { file: string; hash: string }>
      this.manifest = data
      return data
    } catch {
      return null
    }
  }

  /**
   * Get cache buster for a page id using manifest; fall back to hashing file over network.
   */
  private async getCacheBusterForPage(pageName: string): Promise<string> {
    if (import.meta.env.DEV) {
      return String(Date.now())
    }
    const manifest = await this.loadManifest()
    const hash = manifest?.[pageName]?.hash
    if (hash) {
      // Use a short hash for brevity as before
      return hash.substring(0, 8)
    }
    // Fallback: compute from file directly (will cause an extra request)
    const filePath = `/_cache/static-pages/${pageName}.json`
    return this.getCacheBuster(filePath)
  }

  /**
   * Register a static page with markdown content
   */
  registerPage(id: string, title: string, markdownContent: string): void {
    const htmlContent = this.convertMarkdownToHtml(markdownContent)
    const extractedTitle = this.extractTitleFromHtml(htmlContent)

    const page: StaticPage = {
      id,
      title: extractedTitle || title,
      htmlContent,
    }
    this.pages.set(id, page)
  }

  /**
   * Get a static page by ID
   */
  getPage(id: string): StaticPage | undefined {
    return this.pages.get(id)
  }

  /**
   * Get all registered pages
   */
  getAllPages(): StaticPage[] {
    return Array.from(this.pages.values())
  }

  // (Deprecated) Email obfuscation helper removed; logic now lives in replaceTemplateVariables

  /**
   * Replace template variables in content
   */
  private replaceTemplateVariables(content: string): string {
    const email = import.meta.env.VITE_CONTACT_EMAIL

    if (!email) {
      return content
    }

    const encoded = btoa(email).replace(/=/g, '')

    // Create the obfuscated email HTML using a span with data attribute (Vue will handle the decoding)
    const obfuscatedEmailHtml = `<span class="obfuscated-email" data-encoded="${encoded}"></span>`

    // Replace HTML anchor tags that contain the template variable - try multiple patterns
    const patterns = [
      // Standard pattern
      /<a href="mailto:\$\('VITE_CONTACT_EMAIL'\)">\$\('VITE_CONTACT_EMAIL'\)<\/a>/g,
      // Pattern with escaped quotes
      /<a href=&quot;mailto:\$\(&#x27;VITE_CONTACT_EMAIL&#x27;\)&quot;>\$\(&#x27;VITE_CONTACT_EMAIL&#x27;\)<\/a>/g,
      // More flexible pattern
      /<a[^>]*href[^>]*mailto[^>]*VITE_CONTACT_EMAIL[^>]*>[^<]*VITE_CONTACT_EMAIL[^<]*<\/a>/g,
    ]

    patterns.forEach((pattern) => {
      content = content.replace(pattern, obfuscatedEmailHtml)
    })

    // Handle plain template format: $('VITE_CONTACT_EMAIL')
    content = content.replace(/\$\('VITE_CONTACT_EMAIL'\)/g, obfuscatedEmailHtml)

    return content
  }

  /**
   * Decode HTML entities in text
   */
  private decodeHtmlEntities(text: string): string {
    const entityMap: { [key: string]: string } = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&apos;': "'",
    }

    return text.replace(/&[a-zA-Z0-9#]+;/g, (entity) => {
      return entityMap[entity] || entity
    })
  }

  /**
   * Extract title from HTML content by finding the first H1 tag
   */
  private extractTitleFromHtml(htmlContent: string): string | null {
    const h1Match = htmlContent.match(/<h1[^>]*>(.*?)<\/h1>/i)
    if (h1Match && h1Match[1]) {
      // Strip any HTML tags from the H1 content and decode HTML entities
      const strippedText = h1Match[1].replace(/<[^>]*>/g, '').trim()
      return this.decodeHtmlEntities(strippedText)
    }
    return null
  }

  /**
   * Convert markdown to HTML (or return raw markdown for display)
   */
  private convertMarkdownToHtml(markdown: string): string {
    // First convert markdown to HTML, then replace template variables in the HTML
    const htmlContent = marked.parse(markdown) as string
    return this.replaceTemplateVariables(htmlContent)
  }

  /**
   * Load markdown content from file path (for development) or pre-built HTML (for production)
   */
  async loadMarkdownFile(filePath: string): Promise<string> {
    // In development mode, load the markdown file directly
    if (import.meta.env.DEV) {
      const response = await fetch(filePath)
      if (!response.ok) {
        throw new Error(`Failed to load markdown file: ${response.status}`)
      }
      return await response.text()
    } else {
      // In production, try to load pre-built JSON first, then fall back to markdown
      const jsonFilePath = filePath.replace('.md', '.json').replace('/src/', '/_cache/')
      let response = await fetch(jsonFilePath)
      if (response.ok) {
        const pageData = await response.json()
        return pageData.htmlContent || pageData.content || ''
      }

      // Fallback to build-time converted markdown
      response = await fetch(filePath)
      if (!response.ok) {
        throw new Error(`Failed to load markdown file: ${response.status}`)
      }
      return await response.text()
    }
  }

  /**
   * Load a pre-rendered JSON file from the public directory (production pre-rendered files)
   */
  async loadPreRenderedFile(pageName: string): Promise<StaticPage | null> {
    const filePath = `/_cache/static-pages/${pageName}.json`
    const v = await this.getCacheBusterForPage(pageName)
    const response = await fetch(`${filePath}?v=${encodeURIComponent(v)}`)
    if (!response.ok) {
      // Use centralized logging instead of console
      logService.warn(LOG_CATEGORIES.UI, 'Pre-rendered static page not found', {
        pageName,
        status: response.status,
      })
      return null
    }
    const pageData = await response.json()
    const htmlContent = this.replaceTemplateVariables(pageData.htmlContent || '')
    const extractedTitle = this.extractTitleFromHtml(htmlContent)

    return {
      id: pageData.id || pageName,
      title: extractedTitle || pageData.title || pageName,
      htmlContent,
    }
  }

  /**
   * Register page from markdown file
   */
  async registerPageFromFile(id: string, title: string, filePath: string): Promise<void> {
    if (import.meta.env.DEV) {
      // Development: load markdown and convert to HTML
      const markdownContent = await this.loadMarkdownFile(filePath)
      this.registerPage(id, title, markdownContent)
    } else {
      // Production: try to load pre-rendered JSON first, then fall back to build-time converted markdown
      let pageData = await this.loadPreRenderedFile(id)

      if (pageData && pageData.htmlContent) {
        // Apply template replacement to pre-rendered content
        pageData.htmlContent = this.replaceTemplateVariables(pageData.htmlContent)
      }

      if (!pageData) {
        // Fallback to build-time converted markdown
        const htmlContent = await this.loadMarkdownFile(filePath)
        pageData = {
          id,
          title,
          htmlContent: this.replaceTemplateVariables(htmlContent),
        }
      }

      if (pageData) {
        this.pages.set(id, pageData)
      }
    }
  }

  /**
   * Get or load a page dynamically (used by routes)
   */
  async getOrLoadPage(id: string): Promise<StaticPage | null> {
    // Check if page is already registered
    let page = this.getPage(id)

    if (!page) {
      // Try to load the page dynamically
      const title = id.replace(/[-_]+/g, ' ').replace(/\b([a-z])/g, (m) => m.toUpperCase())

      if (import.meta.env.DEV) {
        // Development: load from markdown file
        const filePath = `/src/static-pages/${id}.md`
        await this.registerPageFromFile(id, title, filePath)
      } else {
        // Production: try pre-rendered JSON first, then build-time converted
        let pageData = await this.loadPreRenderedFile(id)

        if (pageData && pageData.htmlContent) {
          // Apply template replacement to pre-rendered content as well
          pageData.htmlContent = this.replaceTemplateVariables(pageData.htmlContent)
          const extractedTitle = this.extractTitleFromHtml(pageData.htmlContent)
          if (extractedTitle) {
            pageData.title = extractedTitle
          }
        }

        if (!pageData) {
          // Try build-time converted markdown
          const filePath = `/src/static-pages/${id}.md`
          const htmlContent = await this.loadMarkdownFile(filePath)
          const processedHtmlContent = this.replaceTemplateVariables(htmlContent)
          const extractedTitle = this.extractTitleFromHtml(processedHtmlContent)

          pageData = {
            id,
            title: extractedTitle || title,
            htmlContent: processedHtmlContent,
          }
        }

        if (pageData) {
          this.pages.set(id, pageData)
        }
      }

      page = this.getPage(id)
    }

    return page || null
  }
}

// Export singleton instance
export const staticPageService = new StaticPageService()
